import type { MeResponse } from '@crm/contracts';
import {
  BellRing,
  Briefcase,
  CalendarClock,
  ChevronRight,
  ClipboardList,
  GraduationCap,
  LayoutDashboard,
  Lightbulb,
  ListChecks,
  ListTodo,
  LogOut,
  MessageSquareText,
  PhoneMissed,
  Play,
  Plus,
  Search,
  Settings,
  Store,
  Sunrise,
  Target,
  TrendingUp,
  UserCog,
  Users,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { signOut } from '@/auth/supabase';
import { NeedsAttentionNavBadge } from '@/components/needs-attention-nav-badge';
import { NotificationBell } from '@/components/notification-bell';
import { Avatar } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { useToast } from '@/components/ui/toast';
import { useDataSourceSummaries } from '@/features/data-sources/use-data-sources';
import { IncomingCallBanner } from '@/features/realtime/incoming-call-banner';
import { ALERT_SOUNDS, type AlertSound, useAlertSound } from '@/features/realtime/use-alert-sound';
import { type DesktopNotifications, useDesktopNotifications } from '@/features/realtime/use-desktop-notifications';
import { useLiveEvents } from '@/features/realtime/use-live-events';
import { useReminderAlerts } from '@/features/realtime/use-reminder-alerts';
import { cn } from '@/lib/utils';
import { BottomNav } from './bottom-nav';
import { NewLeadFab } from './new-lead-fab';

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  end?: boolean;
  /** Hidden unless the signed-in operator is an admin. */
  adminOnly?: boolean;
}

/**
 * FIVE PLACES AN AGENT GOES, and everything else behind one word.
 *
 * The rail had thirteen entries, and an agent's whole day happens in four of
 * them. The other nine are a manager's screens, an admin's screens and
 * reference lists — each individually reasonable, and together a wall that
 * makes the four hard to find. Thirteen equally weighted choices is not a
 * menu, it is a search problem.
 *
 * Nothing is REMOVED. "More" is a disclosure, open by default for admins
 * (whose job is in there) and closed for everyone else, and every route
 * still works for anyone who follows a link.
 */
const NAV: NavItem[] = [
  /* The summary first, the queue second — the order of a morning. */
  { to: '/', label: 'Summary', icon: Sunrise, end: true },
  { to: '/today', label: 'Today', icon: ListTodo },
  { to: '/leads', label: 'Leads', icon: Target },
  { to: '/contacts', label: 'Contacts', icon: Users },
  /* Beside Follow-ups, not under Leads: a missed call nobody returned is
     owed work, which is what this part of the rail is. */
  { to: '/calls', label: 'Calls', icon: PhoneMissed },
  { to: '/follow-ups', label: 'Follow-ups', icon: CalendarClock },
];

/**
 * The rest, behind "More".
 *
 * `/reports` is the Dashboard, moved. It is a reporting screen — counts,
 * charts, a marketing funnel — and it was the first thing an agent saw every
 * morning, which is right for the one person who reads the numbers and
 * backwards for the five who work the queue.
 */
const MORE: NavItem[] = [
  /* A daily operations record rather than a report — but it sits beside
     Reports because it answers a counting question, and the main rail is
     deliberately the five screens an agent works from. */
  { to: '/lead-sheet', label: 'Lead sheet', icon: ClipboardList },
  /* Shown to everyone, not just managers: the owner's decision was that an
     agent reads their own score and their own feedback. Hiding the screen
     would have left the coaching written for them unreadable by them. */
  /* Beside the Coach because they read the same analyses — but this one is a
     work queue and that one is a reading screen. */
  { to: '/priority-calls', label: 'Priority calls', icon: ListChecks },
  { to: '/coaching', label: 'AI Coach', icon: GraduationCap },
  { to: '/reports', label: 'Reports', icon: LayoutDashboard },
  { to: '/deals', label: 'Deals', icon: Briefcase },
  { to: '/templates', label: 'Templates', icon: MessageSquareText },
  { to: '/requirements', label: 'Requirements', icon: Lightbulb },
  { to: '/sellers', label: 'Sellers', icon: Store },
  /* Targets and per-person performance: a manager's screen, not an agent's. */
  { to: '/team', label: 'Team', icon: TrendingUp, adminOnly: true },
  /**
   * §14. Hidden from non-admins rather than shown and refused: everything on
   * that page decides what OTHER people are allowed to see, so offering it to
   * someone who cannot use it is just a dead end in the sidebar. The route
   * still exists for anyone who follows a link, and explains itself there.
   */
  { to: '/users', label: 'Operators', icon: UserCog, adminOnly: true },
  /**
   * Shown to EVERYONE, unlike Operators above.
   *
   * It stopped being an administrator's screen the moment it carried two
   * things an agent owns: which timezone their clock runs in, and who new
   * leads from calls belong to. Hiding the link would have left both
   * unreachable for the person they exist for. The business configuration
   * inside is still admin-only — it is simply not rendered for anyone else,
   * rather than shown and refused.
   */
  { to: '/settings', label: 'Settings', icon: Settings },
];

/**
 * The links and the account block, shared by the desktop rail and the phone
 * drawer. Written once so the two cannot drift — a menu that is missing an
 * item on one of them is the kind of thing nobody notices for a month.
 */
const linkClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    'flex items-center gap-3 rounded-[var(--radius-control)] px-3 py-2 text-sm font-semibold transition-colors',
    isActive ? 'bg-brand-soft text-brand' : 'text-ink-2 hover:bg-surface-2 hover:text-ink',
  );

function NavItemLink({ item }: { item: NavItem }) {
  const { to, label, icon: Icon, end } = item;
  return (
    <NavLink to={to} end={end} className={linkClass}>
      <Icon className="size-[18px]" aria-hidden="true" />
      {label}
      {/*
        On Today, because that is where the work is now — the badge is the
        count of leads needing attention, and the Inbox block below it is
        the list that count came from.

        It FOLLOWED Today to /today rather than staying on the index. The
        Summary already states the same numbers in words and in tiles, so a
        badge there would be a third rendering of one count beside two
        others — and it would send somebody to a page that counts the work
        rather than to the page that works it.
      */}
      {to === '/today' && <NeedsAttentionNavBadge />}
    </NavLink>
  );
}

function NavBody({
  nav,
  more,
  me,
  onSignedOut,
}: {
  nav: NavItem[];
  more: NavItem[];
  me: MeResponse;
  onSignedOut: () => void;
}) {
  return (
    <>
      <nav className="flex flex-1 flex-col gap-0.5 overflow-y-auto px-3 py-2" aria-label="Main">
        {nav.map((item) => (
          <NavItemLink key={item.to} item={item} />
        ))}

        {/*
          EVERYTHING ELSE, behind one word.
          
          `<details>` rather than state: the browser remembers nothing
          either way, and an admin — whose job is mostly in here — gets it
          open while an agent gets five links and a word.
        */}
        {more.length > 0 && (
          <details className="group mt-1" open={me.role === 'admin'}>
            <summary className="flex cursor-pointer list-none items-center gap-3 rounded-[var(--radius-control)] px-3 py-2 text-sm font-semibold text-ink-2 hover:bg-surface-2 hover:text-ink [&::-webkit-details-marker]:hidden">
              <ChevronRight
                className="size-[18px] transition-transform group-open:rotate-90 motion-reduce:transition-none"
                aria-hidden="true"
              />
              More
            </summary>
            <div className="flex flex-col gap-0.5">
              {more.map((item) => (
                <NavItemLink key={item.to} item={item} />
              ))}
            </div>
          </details>
        )}
      </nav>
      <div className="border-t border-line p-3">
        <div className="flex items-center gap-3 px-2 py-1.5">
          <Avatar name={me.fullName} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-ink">{me.fullName}</p>
            <p className="truncate text-xs text-muted">{me.email}</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="mt-1 w-full justify-start"
          onClick={async () => {
            await signOut();
            onSignedOut();
          }}
        >
          <LogOut /> Sign out
        </Button>
      </div>
    </>
  );
}

/**
 * Sound on/off, and the one-time offer of OS notifications.
 *
 * Beside the bell rather than buried in Settings, because the person who
 * needs it muted is the person sitting next to somebody on a call — and
 * because an "Enable alerts" button nobody can find is a permission nobody
 * grants.
 */
function AlertControls({ sound, desktop }: { sound: AlertSound; desktop: DesktopNotifications }) {
  return (
    <>
      {/*
        Only while it is worth asking. Once granted there is nothing to do,
        and once denied the browser will not ask again however many times
        this is pressed — so offering it would be a button that lies.
      */}
      {desktop.canAsk && (
        <Button variant="ghost" size="sm" onClick={() => void desktop.ask()} title="Get reminders as desktop alerts">
          <BellRing className="size-4" /> Enable alerts
        </Button>
      )}

      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Reminder sound"
            title={sound.muted ? 'Reminder sounds are off' : `Reminder sound: ${sound.choice.label}`}
          >
            {sound.muted ? <VolumeX /> : <Volume2 />}
          </Button>
        </PopoverTrigger>

        <PopoverContent align="end" className="w-72 p-0">
          <div className="flex items-center justify-between gap-3 border-b border-line px-3 py-2.5">
            <span className="text-sm font-bold text-ink">Reminder sound</span>
            <Button variant="ghost" size="sm" onClick={() => sound.setMuted(!sound.muted)}>
              {sound.muted ? 'Turn on' : 'Mute'}
            </Button>
          </div>

          {/*
            Clicking a row BOTH selects and plays it. Choosing a sound you
            cannot hear first is choosing from a list of words.
          */}
          <ul className="max-h-72 overflow-y-auto p-1">
            {ALERT_SOUNDS.map((option) => {
              const active = option.id === sound.choice.id;
              return (
                <li key={option.id}>
                  <button
                    type="button"
                    onClick={() => {
                      sound.setChoice(option.id);
                      sound.preview(option.id);
                    }}
                    className={cn(
                      'flex w-full items-start gap-2.5 rounded-[var(--radius-control)] px-2.5 py-2 text-left transition-colors',
                      active ? 'bg-brand-soft' : 'hover:bg-surface-2',
                    )}
                  >
                    <Play className={cn('mt-0.5 size-3.5 shrink-0', active ? 'text-brand' : 'text-muted')} />
                    <span className="min-w-0">
                      <span className={cn('block text-sm font-semibold', active ? 'text-brand' : 'text-ink')}>
                        {option.label}
                      </span>
                      <span className="block text-[12px] leading-snug text-muted">{option.hint}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>

          {/*
            The case the design doc called out: a tab nobody has clicked.
            Audio is locked until a gesture, and saying so beats a chooser
            whose previews are silent for no visible reason.
          */}
          {!sound.unlocked && (
            <p className="border-t border-line px-3 py-2 text-[12px] text-muted">
              Click anywhere in the page once to let this browser play sounds.
            </p>
          )}
          {sound.muted && (
            <p className="border-t border-line px-3 py-2 text-[12px] text-muted">
              Sounds are off. Reminders still appear on screen and in the bell.
            </p>
          )}
        </PopoverContent>
      </Popover>
    </>
  );
}

export function AppShell({ me, onSignedOut }: { me: MeResponse; onSignedOut: () => void }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  /** Phone only: the search box replaces the logo while it is open. */
  const [searchOpen, setSearchOpen] = useState(false);

  // Closing on navigation is the whole contract of a drawer: tapping a link
  // that leaves it open looks like the tap did nothing.
  // biome-ignore lint/correctness/useExhaustiveDependencies: closes on route change, nothing else
  useEffect(() => setMenuOpen(false), [pathname]);

  /**
   * Lists this tenant configured for itself.
   *
   * "Sellers" is ePettyshop's list, fed from their own application; another
   * tenant's will be something else entirely. A tenant that configures a list
   * under the key `sellers` replaces the built-in one rather than getting two
   * entries with the same name.
   */
  /*
   * ONE socket per tab, opened here and handed down. A second component
   * calling `useLiveEvents` would open a second connection to the same
   * server for the same person.
   */
  const live = useLiveEvents(true);
  const sound = useAlertSound();
  const desktop = useDesktopNotifications();
  const toast = useToast();
  useReminderAlerts(live.notification, me.id, sound, desktop, toast);

  const { data: lists } = useDataSourceSummaries();
  const configured = (lists ?? [])
    .filter((l) => l.display.showInSidebar)
    .sort((a, b) => (a.display.sidebarOrder ?? 50) - (b.display.sidebarOrder ?? 50));

  const visible = (items: NavItem[]) =>
    items.filter(
      (item) =>
        (!item.adminOnly || me.role === 'admin') &&
        // `/sellers` steps aside for a configured list keyed `sellers`.
        !configured.some((l) => l.key === item.to.slice(1)),
    );

  const nav = visible(NAV);
  /*
   * Configured lists live under More with the rest of the reference
   * screens. They are things to look things up IN, not work to do.
   */
  const more = [
    ...visible(MORE),
    ...configured.map((l): NavItem => ({ to: `/lists/${l.key}`, label: l.label, icon: ListChecks })),
  ];

  return (
    <div className="flex min-h-full">
      {/*
        The same nav twice: a fixed rail from `md` up, and a drawer below it.
        On a phone there was no navigation at all — the rail was `hidden` and
        nothing replaced it, so every page but the one you landed on was
        unreachable.
      */}
      <aside className="hidden w-60 shrink-0 flex-col border-r border-line bg-surface md:sticky md:top-0 md:flex md:h-screen">
        <div className="flex h-16 items-center px-5">
          <img src="/logo.png" alt="ePettyshop" className="h-7 w-auto" />
        </div>
        <NavBody nav={nav} more={more} me={me} onSignedOut={onSignedOut} />
      </aside>

      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side="left" title="Menu" description="Go to any part of the CRM." className="w-72 p-0">
          <NavBody nav={nav} more={more} me={me} onSignedOut={onSignedOut} />
        </SheetContent>
      </Sheet>

      <div className="flex min-w-0 flex-1 flex-col">
        {/*
          Stays put while the page scrolls. On a phone this is where the menu
          button lives, so letting it scroll away meant scrolling back to the
          top before you could go anywhere. z-30 keeps it under the dialogs
          (50), the toasts (60) and the New lead button (40).
        */}
        <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-line bg-surface px-4 md:px-6">
          {/*
            The logo stays. A phone header that is nothing but a search box
            has no brand and no anchor, and the box does not need the whole
            bar — on a phone search is a button that opens one.
          */}
          <img
            src="/logo.png"
            alt="ePettyshop"
            className={cn('h-6 w-auto shrink-0 md:hidden', searchOpen && 'hidden')}
          />

          <search className={cn('min-w-0 flex-1', searchOpen ? 'block' : 'hidden md:block')}>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const q = new FormData(e.currentTarget).get('q');
                setSearchOpen(false);
                navigate(`/contacts?search=${encodeURIComponent(String(q ?? ''))}`);
              }}
            >
              <label className="sr-only" htmlFor="global-search">
                Search contacts
              </label>
              <Input
                id="global-search"
                name="q"
                type="search"
                placeholder="Search name, email or phone…"
                leading={<Search className="size-[18px]" />}
                className="h-10 md:max-w-md"
                // Opened by a tap, so the keyboard should follow without a second one.
                autoFocus={searchOpen}
                onBlur={(e) => !e.target.value && setSearchOpen(false)}
              />
            </form>
          </search>
          <div className="ml-auto flex shrink-0 items-center gap-1">
            {!searchOpen && (
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden"
                aria-label="Search"
                onClick={() => setSearchOpen(true)}
              >
                <Search />
              </Button>
            )}
            <AlertControls sound={sound} desktop={desktop} />
            <NotificationBell />
            {/*
              "New lead" LIVES HERE ON DESKTOP, not in the corner.

              The floating button is fixed to the viewport, so no amount of
              page padding moves it off the content underneath: on the lead
              page it sat across the "What happened" box and the submit button
              of the close form, at every scroll position. A phone has no
              sidebar and needs the floating one; a desktop already has the
              nav rail, so the button belongs in the header where it covers
              nothing. See new-lead-fab.tsx, which hides itself above `md`.
            */}
            {!searchOpen && (
              <Button asChild size="sm" className="ml-1 hidden md:inline-flex">
                <Link to="/leads/new">
                  <Plus /> New lead
                </Link>
              </Button>
            )}
          </div>
        </header>

        <main className="min-w-0 flex-1 p-4 pb-[calc(8rem+env(safe-area-inset-bottom))] md:p-6 md:pb-6">
          <Outlet />
        </main>
      </div>

      <BottomNav onOpenMenu={() => setMenuOpen(true)} />

      {/* Last in the DOM, so it is last in the tab order: a shortcut should
          not come before the page's own content. */}
      <NewLeadFab />

      {/* Inside the shell, so it exists only while signed in — the socket it
          opens needs a token, and there is nobody to tell when there is no
          session. */}
      <IncomingCallBanner call={live.call} />
    </div>
  );
}
